/**
 * Componente: Selector de Idioma Tipo Píldora — LanguageSelectorPill (Apple HIG Style)
 * Estilo visual refinado tipo Apple con CSS Modules garantizados:
 * - Botón píldora con fondo blanco puro, borde nítido y sombra multicapa Apple.
 * - Microinteracciones táctiles suaves (hover, active y focus ring).
 * - Menú popover flotante con esquinas redondeadas continuas y selección verde AviFácil.
 */

'use client';

import React, { useState, useRef, useEffect } from 'react';
import * as Flags from 'country-flag-icons/react/3x2';
import { ChevronDown, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './LanguageSelectorPill.module.css';

export interface LanguageOption {
  codigo: 'es' | 'en' | 'pt';
  label: string;
  banderaIso: 'ES' | 'US' | 'BR';
}

export const IDIOMAS_DISPONIBLES: LanguageOption[] = [
  { codigo: 'es', label: 'Español', banderaIso: 'ES' },
  { codigo: 'en', label: 'English', banderaIso: 'US' },
  { codigo: 'pt', label: 'Português', banderaIso: 'BR' },
];

interface LanguageSelectorPillProps {
  value?: 'es' | 'en' | 'pt';
  onChange?: (lang: 'es' | 'en' | 'pt') => void;
  className?: string;
}

export function LanguageSelectorPill({
  value = 'es',
  onChange,
  className = '',
}: LanguageSelectorPillProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState<'es' | 'en' | 'pt'>(value);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedLang(value);
  }, [value]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const currentOption =
    IDIOMAS_DISPONIBLES.find((opt) => opt.codigo === selectedLang) || IDIOMAS_DISPONIBLES[0];

  const handleSelect = (codigo: 'es' | 'en' | 'pt') => {
    setSelectedLang(codigo);
    onChange?.(codigo);
    setIsOpen(false);
  };

  const renderFlag = (iso: string) => {
    const FlagComp = (
      Flags as Record<string, React.ComponentType<{ style?: React.CSSProperties; className?: string }>>
    )[iso];
    if (!FlagComp) return null;
    return (
      <span className={styles.flagBadge}>
        <FlagComp
          style={{
            width: '18px',
            height: '12px',
            display: 'block',
          }}
        />
      </span>
    );
  };

  return (
    <div ref={containerRef} className={`${styles.container} ${className}`}>
      {/* Botón Píldora Apple HIG */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`${styles.pillButton} ${isOpen ? styles.isOpen : ''}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {renderFlag(currentOption.banderaIso)}
        <span className={styles.label}>{currentOption.label}</span>
        <ChevronDown
          size={14}
          strokeWidth={2.2}
          className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
        />
      </button>

      {/* Menú Desplegable Flotante Popover */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: -6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: -6 }}
            transition={{ type: 'spring', damping: 25, stiffness: 350 }}
            className={styles.dropdownPopover}
            role="listbox"
          >
            {IDIOMAS_DISPONIBLES.map((idioma) => {
              const isSelected = idioma.codigo === selectedLang;
              return (
                <button
                  key={idioma.codigo}
                  type="button"
                  onClick={() => handleSelect(idioma.codigo)}
                  className={`${styles.optionItem} ${isSelected ? styles.isSelected : ''}`}
                  role="option"
                  aria-selected={isSelected}
                >
                  <div className={styles.optionContent}>
                    {renderFlag(idioma.banderaIso)}
                    <span>{idioma.label}</span>
                  </div>
                  {isSelected && (
                    <motion.div
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.15 }}
                    >
                      <Check size={14} strokeWidth={2.8} className={styles.checkIcon} />
                    </motion.div>
                  )}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
